import {
  ActionIcon,
  Button,
  Card,
  Group,
  LoadingOverlay,
  NumberInput,
  SegmentedControl,
  Select,
  Tabs,
  Text,
  Tooltip,
} from '@mantine/core'
import { FaSave } from 'react-icons/fa'
import FocusComparisonCards from './FocusComparisonCards'
import { Rectangle } from './types'
import { RiResetLeftFill } from 'react-icons/ri'
import { useTranslation } from 'react-i18next'
import { CameraPosition } from '@/api/controlUnitApi/types.gen'
import { VehicleService } from '@/api-proxy/serviceAvailability'
import { CAMERA_KEYS } from './helpers'

const FocusPanel = ({
  originalImageUrl,
  adjustedImageUrl,
  selectedSquare,
  cameras,
  camera,
  setCamera,
  focusIncrement,
  setFocusIncrement,
  focusValue,
  setFocusValue,
  focusLoading,
  displaySaveButton,
  onSave,
  onResetToInitial,
  initialFocusValue,
  isOriginalLoading,
  isAdjustedLoading,
  onRefetchOriginal,
  onRefetchAdjusted,
  unreachableServices = [],
  onRetryUnreachable = () => undefined,
}: {
  cameras: CameraPosition[]
  camera: CameraPosition
  setCamera: (value: CameraPosition) => void
  focusIncrement: number
  setFocusIncrement: (value: number) => void
  focusValue?: number
  setFocusValue: (value: number) => void
  focusLoading: boolean
  displaySaveButton: boolean
  onSave: () => void
  originalImageUrl?: string
  adjustedImageUrl?: string
  selectedSquare?: Rectangle | null
  onSquareChange: (square: Rectangle | null) => void
  isLoading: boolean
  onResetToInitial?: () => void
  initialFocusValue?: number | null
  isOriginalLoading: boolean
  isAdjustedLoading: boolean
  onRefetchOriginal: () => void
  onRefetchAdjusted: () => void
  unreachableServices?: VehicleService[]
  onRetryUnreachable?: () => void
}) => {
  const { t } = useTranslation(['hardware', 'common'])
  return (
    <Tabs.Panel value="focus">
      <Card shadow="sm" p="lg" radius="md" withBorder mt="lg">
        <Text mb="xs">{t('selectCamera')}</Text>
        <Select
          mb="md"
          maw={300}
          data={cameras.map(position => ({ label: t(CAMERA_KEYS[position].name), value: position }))}
          value={camera}
          onChange={value => value && setCamera(value as CameraPosition)}
        />
        <Text mb="xs">{t('selectFocusIncrement')}</Text>
        <Group justify="flex-start" mb="md">
          <SegmentedControl
            value={focusIncrement.toString()}
            onChange={value => setFocusIncrement(parseInt(value))}
            data={[
              { label: '10', value: String(10) },
              { label: '20', value: String(20) },
              { label: '50', value: String(50) },
              { label: '100', value: String(100) },
              { label: '200', value: String(200) },
            ]}
          />
          <NumberInput
            value={focusValue}
            placeholder={t('focusPlaceholder')}
            clampBehavior="strict"
            min={0}
            max={9118}
            step={focusIncrement}
            disabled={focusValue === undefined}
            onChange={value => setFocusValue(Number(value))}
          />
          <LoadingOverlay visible={focusLoading} />
          {onResetToInitial && initialFocusValue !== null && (
            <Tooltip label={t('resetTo', { value: initialFocusValue })} arrowOffset={10} position="top" withArrow>
              <ActionIcon
                size={36}
                variant="default"
                aria-label="Collapse"
                onClick={onResetToInitial}
                disabled={focusValue === initialFocusValue}
              >
                <RiResetLeftFill size={20} />
              </ActionIcon>
            </Tooltip>
          )}
          {displaySaveButton && (
            <Button leftSection={<FaSave />} onClick={onSave}>
              {t('save', { ns: 'common' })}
            </Button>
          )}
        </Group>
        <FocusComparisonCards
          originalImageUrl={originalImageUrl}
          adjustedImageUrl={adjustedImageUrl}
          selectedRectangle={selectedSquare}
          isOriginalLoading={isOriginalLoading}
          isAdjustedLoading={isAdjustedLoading}
          onRefetchOriginal={onRefetchOriginal}
          onRefetchAdjusted={onRefetchAdjusted}
          unreachableServices={unreachableServices}
          onRetry={onRetryUnreachable}
        />
      </Card>
    </Tabs.Panel>
  )
}

export default FocusPanel
